import type { Vec2 } from '../../../geo/types';
import { ROLES } from '../citizens/citizenActing';
import { CROWD, STAYS } from './crowdConfig';
import type { BakedCrowd } from './crowdPlan';
import { PLACES } from './crowdPlan';
import { placesOf, seatsOf, type Residents } from './residents';
import { routeTree } from './routes';
import { seatInTurn } from './seating';
import { KEEPS } from './seats';
import { alongLine, lineOf, type OnLine, type WalkLine } from './walkLine';
import type { WalkNet } from './walkNet';

/** Everyone out of doors at one moment, one entry each, in the order of the residents. */
export interface CrowdFrame {
  count: number;
  readonly resident: Int32Array;
  /** Layout metres, and the ground's height under the figure. */
  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly ground: Float32Array;
  readonly heading: Float32Array;
  /** Metres walked on this outing: what the gait is drawn from. */
  readonly walked: Float32Array;
  readonly moving: Uint8Array;
  /** One that stands turned to its way home, about to walk on, and no longer as its seat is. */
  readonly leaving: Uint8Array;
  /** Which of the residents' outings this is. */
  readonly outing: Int32Array;
  /** Seconds since the figure came to a stand or walked on from one; endless on the way out. */
  readonly turned: Float32Array;
  /** What it does where it stands (ROLES); it is still about it for a moment as it walks on. */
  readonly role: Uint8Array;
  /** Who stands in its company, itself among them: those who came, and sixteen times those who
   *  keep the place (COMPANY). */
  readonly met: Uint8Array;
}

/** How a company is counted into one number: those who came in its low bits, those who keep the
 *  place above them. */
export const COMPANY = { keeper: 16, most: 15 } as const;

/* One who walks on is still about what it was doing for this long: it does not snap out of it. */
const PARTING_S = 1;

/** How a resident walks its way. Every number is an estimate of what reads as walking. */
export const WALKING = {
  /** It rounds a corner of its way from this far before it, in metres. */
  cornerM: 0.6,
  /** It takes this long to come to its pace and to lose it again, in seconds. */
  rampS: 0.6,
  /** It turns to its way home this long before it leaves where it stands. */
  turnS: 1,
  /** The cosine of the turn at its place's point beyond which it walks straight to its stand. */
  spurCos: Math.cos((100 * Math.PI) / 180),
} as const;

/** The ground covered `seconds` into a walk of `metres` that takes `whole` seconds, its pace
 *  gathered at its start and lost at its end. A walk is given the ramp's length more than its
 *  metres take at the walker's pace (spanOf), so between the two it goes at that pace and no
 *  faster. Its feet are drawn from this, so they slow as it does. */
export function walkedBy(seconds: number, whole: number, metres: number): number {
  const ramp = Math.min(WALKING.rampS, whole / 2);
  if (ramp <= 0 || whole <= 0) return metres;
  const pace = metres / (whole - ramp);
  const t = Math.max(0, Math.min(whole, seconds));
  if (t < ramp) return (pace * t * t) / (2 * ramp);
  if (t > whole - ramp) return metres - (pace * (whole - t) * (whole - t)) / (2 * ramp);
  return pace * (t - ramp / 2);
}

interface Way {
  readonly outing: number;
  readonly out: WalkLine;
  readonly back: WalkLine;
}

export interface CrowdSim {
  /** Finds routes for up to `budgetMs`; true once every place's routes are found. */
  prepare(budgetMs: number): boolean;
  /** Who is out, `seconds` after the day opened, on a day `daySeconds` long. */
  frame(seconds: number, daySeconds: number): CrowdFrame;
}

const SPREAD = 2654435761;
const lot = (n: number): number => (Math.imul(n + 1, SPREAD) >>> 0) / 2 ** 32;

/**
 * The crowd's day. Where a resident is follows from the time alone: each outing has its part of the
 * day, and within it a moment to leave, a walk, a stay and a walk home. Nothing is carried from
 * frame to frame but the lines being walked, so a clock set to another hour, or a still frame,
 * shows the same city a visitor would have found there.
 *
 * Walkers do not see each other. Each keeps to its own lane on the right of its way, so those going
 * and those coming pass on opposite sides, as the cast's own walkers prefer to (crowdMotion.ts).
 * Those who stand do: a company's seats are given out as its people come (seating.ts).
 */
export function createCrowdSim(
  baked: BakedCrowd,
  net: WalkNet,
  residents: Residents,
  heightAt: (p: Vec2) => number
): CrowdSim {
  const places = placesOf(baked);
  const seats = seatsOf(baked);
  const { outings } = residents;
  /* Each place's routes, as every point's next point towards it: 16 bits each while the net has
     under 2^15 points, which halves the megabytes the city's places would otherwise take. */
  const narrow = net.count < 1 << 15;
  const trees: (Int16Array | Int32Array | undefined)[] = places.map(() => undefined);
  /* The outings that go to each place, to give each its length when the place's routes are found. */
  const goingTo: number[][] = places.map(() => []);
  const owner = new Int32Array(outings.count);
  for (let r = 0; r < residents.count; r++)
    for (let o = residents.first[r]!; o < residents.first[r + 1]!; o++) {
      owner[o] = r;
      goingTo[outings.place[o]!]!.push(o);
    }
  /* Where each outing stands, how it is turned and what it does there: as planned, until its
     company's seats are given out for the day (seat). */
  const standX = Float32Array.from(outings.standX),
    standY = Float32Array.from(outings.standY),
    facing = Float32Array.from(outings.facing),
    role = Uint8Array.from(outings.role),
    group = Int32Array.from(outings.group);
  const keeper = ROLES.map((one) => KEEPS.has(one));
  /* The seats of each company that anyone may take. */
  const shared: number[][] = Array.from({ length: seats.groups }, () => []);
  for (let s = 0; s < seats.count; s++)
    if (!keeper[seats.role[s]!]) shared[seats.group[s]!]!.push(s);

  const thresholdAt = (d: number): Vec2 => [baked.doors[d * 5]!, baked.doors[d * 5 + 1]!];
  const doorAt = (d: number): Vec2 => [
    baked.doors[d * 5]! - baked.doors[d * 5 + 2]! * CROWD.doorInsideM,
    baked.doors[d * 5 + 1]! - baked.doors[d * 5 + 3]! * CROWD.doorInsideM,
  ];
  const stepAt = (d: number): Vec2 => [
    baked.doors[d * 5]! + baked.doors[d * 5 + 2]! * PLACES.doorStepM,
    baked.doors[d * 5 + 1]! + baked.doors[d * 5 + 3]! * PLACES.doorStepM,
  ];
  const homeNode = (d: number): number => baked.doors[d * 5 + 4]!;
  const nodeGround = new Float32Array(net.count).fill(NaN);
  const groundAtNode = (node: number): number => {
    if (Number.isNaN(nodeGround[node]!)) nodeGround[node] = heightAt([net.x[node]!, net.y[node]!]);
    return nodeGround[node]!;
  };
  /* The ground's height is asked once for each point and each door: the query walks the banks'
     edges about the Dam, and thousands of ways are laid when a day is entered at noon. */
  const doorGround = new Float32Array(baked.doors.length / 5).fill(NaN);
  const groundAtDoor = (d: number): number => {
    if (Number.isNaN(doorGround[d]!)) doorGround[d] = heightAt(stepAt(d));
    return doorGround[d]!;
  };
  let nextPlace = 0;

  /* Visits the points of an outing's way along the net, each moved to the walker's lane: to the
     right of the way there with `side` 1, and of the way home with -1. */
  function eachLaned(
    o: number,
    side: number,
    visit: (x: number, y: number, node: number) => void
  ): void {
    const r = owner[o]!,
      d = residents.door[r]!,
      place = outings.place[o]!;
    const tree = trees[place]!,
      lane = residents.lane[r]!;
    let [beforeX, beforeY] = stepAt(d);
    let [lastX, lastY] = [beforeX, beforeY];
    for (let node = homeNode(d); node >= 0;) {
      const next = tree[node]!;
      const [afterX, afterY] =
        next >= 0
          ? [net.x[next]!, net.y[next]!]
          : [net.x[node]! + standX[o]!, net.y[node]! + standY[o]!];
      const [dx, dy] = [afterX - beforeX, afterY - beforeY];
      const span = Math.hypot(dx, dy) || 1;
      const keep = side * Math.min(lane, net.half[node]!);
      const [x, y] = [net.x[node]! + (dy / span) * keep, net.y[node]! - (dx / span) * keep];
      /* The place's own point, in the walker's lane, can lie past where it stands: it walked
         on to it and turned back on itself for its last steps. It goes straight to its stand. */
      const [inX, inY, onX, onY] = [x - lastX, y - lastY, afterX - x, afterY - y];
      const back =
        next < 0 &&
        inX * onX + inY * onY < WALKING.spurCos * Math.hypot(inX, inY) * Math.hypot(onX, onY);
      if (!back) {
        visit(x, y, node);
        [lastX, lastY] = [x, y];
      }
      [beforeX, beforeY] = [net.x[node]!, net.y[node]!];
      node = next;
    }
  }

  /* The length of the line an outing is walked on, one way: what its timing is reckoned on, so a
     figure covers the ground at its own speed and its feet do not slide. */
  function wayLength(o: number, side: number): number {
    const node = places[outings.place[o]!]!.node;
    const door = residents.door[owner[o]!]!;
    let [x, y] = stepAt(door);
    const inside = doorAt(door);
    let length = Math.hypot(x - inside[0], y - inside[1]);
    const to = (nx: number, ny: number): void => {
      length += Math.hypot(nx - x, ny - y);
      [x, y] = [nx, ny];
    };
    eachLaned(o, side, to);
    to(net.x[node]! + standX[o]!, net.y[node]! + standY[o]!);
    return length;
  }

  function wayOf(o: number): Way {
    const d = residents.door[owner[o]!]!,
      place = places[outings.place[o]!]!;
    const door = doorAt(d),
      step = stepAt(d),
      threshold = thresholdAt(d);
    const stepGround = groundAtDoor(d);
    const entry = baked.doorways?.[d];
    const stand: [number, number, number] = [
      net.x[place.node]! + standX[o]!,
      net.y[place.node]! + standY[o]!,
      groundAtNode(place.node),
    ];
    const laned = (side: number): [number, number, number][] => {
      const points: [number, number, number][] = [];
      eachLaned(o, side, (x, y, node) => points.push([x, y, groundAtNode(node)]));
      return points;
    };
    const start: [number, number, number][] = [
      [door[0], door[1], entry?.groundY ?? stepGround],
      [threshold[0], threshold[1], entry ? entry.groundY + entry.stepHeight : stepGround],
      [step[0], step[1], stepGround],
    ];
    // Out of its door and down its step it walks square; its corners are rounded from there on.
    const outward = [...start, ...laned(1), stand],
      homeward = [stand, ...laned(-1).reverse(), ...[...start].reverse()];
    return {
      outing: o,
      out: lineOf(outward, WALKING.cornerM, start.length),
      back: lineOf(homeward, WALKING.cornerM, 1, homeward.length - 1 - start.length),
    };
  }

  /* When an outing is made on a day `wholeDay` long: the second after the day's opening at which
     its figure steps out, and how long it walks, stays and walks home. False for one that is not
     made: not yet routed, out of reach, or too far to go and come back in its part of the day. */
  const span = { begin: 0, walkOut: 0, stay: 0, walkBack: 0 };
  function spanOf(o: number, wholeDay: number): boolean {
    const r = owner[o]!,
      n = residents.first[r + 1]! - residents.first[r]!;
    const part = (wholeDay * (1 - CROWD.riseSpread)) / n,
      speed = residents.speed[r]!;
    // Each walk is as much longer as it takes to come to its pace and lose it (walkedBy).
    span.walkOut = outings.outM[o]! / speed + WALKING.rampS;
    span.walkBack = outings.backM[o]! / speed + WALKING.rampS;
    span.stay = Math.min(outings.stay[o]!, part - span.walkOut - span.walkBack);
    if (!(span.stay >= CROWD.shortestStayS)) return false;
    const whole = span.walkOut + span.stay + span.walkBack;
    span.begin =
      residents.rise[r]! * CROWD.riseSpread * wholeDay +
      (o - residents.first[r]!) * part +
      outings.start[o]! * (part - whole);
    return true;
  }

  /* Gives out the seats of the companies at a place for a day `wholeDay` long. One who finds its
     company's seats taken stands by, where there is room round the place's point. */
  function seat(p: number, wholeDay: number): void {
    const coming = new Map<number, number[]>();
    for (const o of goingTo[p]!) {
      const planned = outings.group[o]!;
      if (planned < 0 || keeper[outings.role[o]!]) continue;
      if (!coming.has(planned)) coming.set(planned, []);
      coming.get(planned)!.push(o);
    }
    for (const [planned, those] of coming) {
      const free = shared[planned]!;
      const given = seatInTurn(
        those.map((o) =>
          spanOf(o, wholeDay)
            ? { arrive: span.begin + span.walkOut, leave: span.begin + span.walkOut + span.stay }
            : { arrive: NaN, leave: NaN }
        ),
        free.length
      );
      those.forEach((o, i) => {
        const taken = given[i]! >= 0 ? free[given[i]!]! : -1;
        const reach = Math.min(CROWD.standM, places[p]!.room) * Math.sqrt(lot(o));
        const turn = lot(o + outings.count) * Math.PI * 2;
        standX[o] = taken >= 0 ? seats.x[taken]! : Math.cos(turn) * reach;
        standY[o] = taken >= 0 ? seats.y[taken]! : Math.sin(turn) * reach;
        facing[o] = taken >= 0 ? seats.facing[taken]! : NaN;
        role[o] = taken >= 0 ? seats.role[taken]! : 0;
        group[o] = taken >= 0 ? planned : -1;
      });
    }
  }

  const ways: (Way | undefined)[] = Array.from({ length: residents.count }, () => undefined);
  const frame: CrowdFrame = {
    count: 0,
    resident: new Int32Array(residents.count),
    x: new Float32Array(residents.count),
    y: new Float32Array(residents.count),
    ground: new Float32Array(residents.count),
    heading: new Float32Array(residents.count),
    walked: new Float32Array(residents.count),
    moving: new Uint8Array(residents.count),
    leaving: new Uint8Array(residents.count),
    outing: new Int32Array(residents.count),
    turned: new Float32Array(residents.count),
    role: new Uint8Array(residents.count),
    met: new Uint8Array(residents.count),
  };
  /* Each company's count for the frame, the company of each one out, and the companies counted,
     to be cleared for the next frame. */
  const tally = new Uint8Array(seats.groups);
  const company = new Int32Array(residents.count);
  const counted: number[] = [];
  const at: OnLine = { x: 0, y: 0, ground: 0, heading: 0 };
  /* The length of the day the seats are given out for. */
  let seatedDay = NaN;

  function put(slot: number, line: WalkLine, share: number): void {
    alongLine(line, share, at);
    frame.x[slot] = at.x;
    frame.y[slot] = at.y;
    frame.ground[slot] = at.ground;
    frame.heading[slot] = at.heading;
  }

  return {
    prepare(budgetMs) {
      const until = performance.now() + budgetMs;
      while (nextPlace < places.length) {
        const tree = routeTree(net, places[nextPlace]!.node);
        trees[nextPlace] = narrow ? Int16Array.from(tree.next) : tree.next;
        for (const o of goingTo[nextPlace]!) {
          // A place on another piece of the net is never reached, and never set out for.
          const reached = Number.isFinite(tree.distance[homeNode(residents.door[owner[o]!]!)]);
          outings.outM[o] = reached ? wayLength(o, 1) : Infinity;
          outings.backM[o] = reached ? wayLength(o, -1) : Infinity;
        }
        if (!Number.isNaN(seatedDay)) seat(nextPlace, seatedDay);
        nextPlace++;
        if (performance.now() >= until) break;
      }
      return nextPlace >= places.length;
    },

    frame(sinceOpen, wholeDay) {
      if (wholeDay !== seatedDay) {
        seatedDay = wholeDay;
        for (let p = 0; p < nextPlace; p++) seat(p, wholeDay);
        ways.fill(undefined);
      }
      let out = 0;
      for (const counting of counted) tally[counting] = 0;
      counted.length = 0;
      const daySeconds = wholeDay * (1 - CROWD.riseSpread);
      for (let r = 0; r < residents.count; r++) {
        const from = residents.first[r]!,
          n = residents.first[r + 1]! - from;
        const seconds = sinceOpen - residents.rise[r]! * CROWD.riseSpread * wholeDay;
        if (n === 0 || seconds < 0 || seconds >= daySeconds) {
          ways[r] = undefined;
          continue;
        }
        const o = from + Math.min(n - 1, Math.floor(seconds / (daySeconds / n)));
        if (!spanOf(o, wholeDay)) {
          ways[r] = undefined;
          continue;
        }
        const { walkOut, stay, walkBack } = span;
        const since = sinceOpen - span.begin;
        if (since < 0 || since >= walkOut + stay + walkBack) {
          ways[r] = undefined;
          continue;
        }
        const outM = outings.outM[o]!,
          backM = outings.backM[o]!;
        const seen = STAYS[places[outings.place[o]!]!.kind].seen;
        const staying = since >= walkOut && since < walkOut + stay;
        if (staying && !seen) continue;
        let way = ways[r];
        if (way?.outing !== o) way = ways[r] = wayOf(o);
        frame.resident[out] = r;
        frame.outing[out] = o;
        const parting = since >= walkOut + stay && since - walkOut - stay < PARTING_S;
        const among = staying || parting ? group[o]! : -1;
        frame.role[out] = staying || parting ? role[o]! : 0;
        company[out] = among;
        if (staying && among >= 0) {
          const count = tally[among]!;
          if (count === 0) counted.push(among);
          if (!keeper[role[o]!])
            tally[among] = count + (count % COMPANY.keeper < COMPANY.most ? 1 : 0);
          else if (count < COMPANY.keeper * COMPANY.most) tally[among] = count + COMPANY.keeper;
        }
        if (staying) {
          frame.turned[out] = since - walkOut;
          put(out, way.out, 1);
          const node = places[outings.place[o]!]!.node;
          const [tx, ty] = [net.x[node]! - frame.x[out]!, net.y[node]! - frame.y[out]!];
          // Turned as its seat is, or to the place it came to; one standing on the place's very
          // point keeps the way it came.
          if (!Number.isNaN(facing[o]!)) frame.heading[out] = facing[o]!;
          else if (Math.hypot(tx, ty) > 0.3) frame.heading[out] = Math.atan2(ty, tx);
          // About to leave, it turns to its way home where it stands, and then goes.
          frame.leaving[out] = walkOut + stay - since < WALKING.turnS ? 1 : 0;
          if (frame.leaving[out]) frame.heading[out] = alongLine(way.back, 0, at).heading;
          frame.walked[out] = outM;
          frame.moving[out] = 0;
        } else {
          const back = since >= walkOut + stay;
          frame.turned[out] = back ? since - walkOut - stay : Infinity;
          const length = back ? backM : outM;
          const gone = walkedBy(
            back ? since - walkOut - stay : since,
            back ? walkBack : walkOut,
            length
          );
          put(out, back ? way.back : way.out, gone / length);
          frame.walked[out] = back ? outM + gone : gone;
          frame.moving[out] = 1;
          frame.leaving[out] = 0;
        }
        out++;
      }
      for (let i = 0; i < out; i++) frame.met[i] = company[i]! >= 0 ? tally[company[i]!]! : 0;
      frame.count = out;
      return frame;
    },
  };
}
